import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { TreeDeciduous, Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('toasts.emailBitte'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/password-reset/request', { email });
      setSent(true);
    } catch (error) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-50 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="p-2 rounded-lg bg-primary">
            <TreeDeciduous className="h-6 w-6 text-white" />
          </div>
          <span className="font-serif text-2xl font-bold text-primary">ForestHub</span>
        </Link>

          <Card className="border-border/50 shadow-soft">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">{t('auth.resetPassword')}</CardTitle>
              <CardDescription>
                {sent 
                  ? 'Prüfen Sie Ihren Posteingang'
                  : 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-6" data-testid="forgot-password-success">
                  <div className="inline-flex p-4 rounded-full bg-green-100 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="text-muted-foreground mb-2">
                    Falls ein Konto mit <strong>{email}</strong> existiert, haben wir Ihnen 
                    eine E-Mail mit einem Reset-Link gesendet.
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Der Link ist 24 Stunden gültig. Prüfen Sie auch Ihren Spam-Ordner.
                  </p>
                  <Link to="/anmelden">
                    <Button variant="outline">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t('nav.login')}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre@email.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-forest"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('auth.resetPassword')
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
